import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Shell } from '@/components/shell';
import { Page } from '@/components/page';
import { ButtonLink, Card, EmptyState } from '@/components/ui';
import { LoginPage } from '@/pages/login';
import { OverviewPage } from '@/pages/overview';
import { OrdersListPage } from '@/pages/orders/list';
import { OrderDetailPage } from '@/pages/orders/detail';
import { NewOrderPage } from '@/pages/orders/new';
import { PackingSlipsPage } from '@/pages/orders/packing-slips';
import { ProductsListPage } from '@/pages/products/list';
import { ProductEditorPage } from '@/pages/products/editor';
import { ProductGridPage } from '@/pages/products/grid';
import { BrandsPage } from '@/pages/brands';
import { SeoHealthPage } from '@/pages/seo-health';
import { FeedsPage } from '@/pages/feeds';
import { CampaignLinksPage } from '@/pages/campaign-links';
import { PopupsPage } from '@/pages/popups';
import { AutomationsPage } from '@/pages/automations';
import { SecurityPage } from '@/pages/security';
import { SubscribersPage } from '@/pages/subscribers';
import { SeoSettingsPage } from '@/pages/settings/seo';
import { InventoryPage } from '@/pages/inventory';
import { CategoriesPage } from '@/pages/categories';
import { AttributesPage } from '@/pages/attributes';
import { ImportPage } from '@/pages/import';
import { DiscountsPage } from '@/pages/discounts';
import { PriceListsPage } from '@/pages/price-lists';
import { LooksPage } from '@/pages/looks/list';
import { LookEditorPage } from '@/pages/looks/editor';
import { OptionsLibraryPage } from '@/pages/options';
import { SizeChartsPage } from '@/pages/size-charts';
import { RedirectsPage } from '@/pages/redirects';
import { BlogPage } from '@/pages/blog';
import { BlogEditorPage } from '@/pages/blog/editor';
import { GiftCardsPage } from '@/pages/gift-cards';
import { AffiliatesPage } from '@/pages/affiliates';
import { CustomerGroupsPage } from '@/pages/customer-groups';
import { FollowUpsPage } from '@/pages/follow-ups';
import { StockLocationsPage } from '@/pages/stock-locations';
import { TradeApplicationsPage } from '@/pages/trade-applications';
import { CustomersPage } from '@/pages/customers';
import { CustomerDetailPage } from '@/pages/customers/detail';
import { InvoicesPage } from '@/pages/invoices';
import { ReturnsPage } from '@/pages/returns';
import { QuestionsPage } from '@/pages/questions';
import { ReviewsPage } from '@/pages/reviews';
import { PromotionsPage } from '@/pages/promotions';
import { ShopperActivityPage } from '@/pages/shopper-activity';
import { ReportsPage } from '@/pages/reports';
import {
  AdvancedSettingsPage,
  FeatureFlagsPage,
  SettingsHome,
  SettingsLayout,
  SettingsSectionsPage,
} from '@/pages/settings';
import { ContentPagesPage, HomeLayoutPage } from '@/pages/storefront';
import { ShippingPage } from '@/pages/shipping';
import { EmailTemplatesPage } from '@/pages/email-templates';
import { BrandingPage } from '@/pages/branding';
import { NavigationPage } from '@/pages/navigation';
import { ApiKeysPage } from '@/pages/api-keys';
import { WebhooksPage } from '@/pages/webhooks';
import { AiPage } from '@/pages/ai';
import { MessagesPage } from '@/pages/messages';
import { CampaignsPage } from '@/pages/campaigns';
import { IntegrationsPage } from '@/pages/integrations';
import { MessagingPage } from '@/pages/messaging';
import { WhatsAppPage } from '@/pages/whatsapp';
import { SupportPage } from '@/pages/support';
import { PaymentsPage } from '@/pages/payments';
import { TeamPage } from '@/pages/team';
import { AuditPage } from '@/pages/audit';
import { setSessionLostHandler } from '@/lib/api-client';
import { MOVED } from '@/lib/nav';
import { useAuthStore } from '@/stores/auth.store';
import { applyDocument, useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';

/**
 * Routing and the session gate.
 *
 * Screens land here as they are built, in the order 07-Admin/Dashboard.md sets:
 * orders first (the store cannot be run without them), then products and
 * inventory, discounts, the dashboard, and the rest.
 */
export function App() {
  const { user, loading, restore } = useAuthStore();
  const { locale, theme } = useUiStore();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();

  useEffect(() => {
    void restore();
  }, [restore]);

  // Language and theme are document-level attributes: direction, font stack,
  // and colour scheme all hang off <html>, not off a wrapper component.
  useEffect(() => {
    applyDocument(locale, theme);
  }, [locale, theme]);

  useEffect(() => {
    // When a refresh fails mid-session, land on sign-in rather than leaving a
    // screen full of stale data behind an error.
    setSessionLostHandler(() => navigate('/login', { replace: true }));
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-sunken">
        <p className="text-sm text-ink-muted">{t('loading')}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Remember where they were headed so sign-in can return them there. */}
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname }} />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/overview" replace />} />
      {/* Printouts stand outside the shell: the page is the paper, nothing else (ADR-059). */}
      <Route path="/orders/print" element={<PackingSlipsPage />} />

      <Route element={<Shell />}>
        <Route path="/" element={<Navigate to="/overview" replace />} />
        <Route path="/overview" element={<OverviewPage />} />
        <Route path="/orders" element={<OrdersListPage />} />
        <Route path="/orders/new" element={<NewOrderPage />} />
        <Route path="/orders/:id" element={<OrderDetailPage />} />

        <Route path="/products" element={<ProductsListPage />} />
        <Route path="/products/new" element={<ProductEditorPage />} />
        <Route path="/products/grid" element={<ProductGridPage />} />
        <Route path="/brands" element={<BrandsPage />} />
        <Route path="/seo" element={<SeoHealthPage />} />
        <Route path="/feeds" element={<FeedsPage />} />
        <Route path="/campaign-links" element={<CampaignLinksPage />} />
        <Route path="/popups" element={<PopupsPage />} />
        <Route path="/automations" element={<AutomationsPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/subscribers" element={<SubscribersPage />} />
        <Route path="/products/:id" element={<ProductEditorPage />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/attributes" element={<AttributesPage />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/import" element={<ImportPage />} />

        <Route path="/looks" element={<LooksPage />} />
        <Route path="/looks/new" element={<LookEditorPage />} />
        <Route path="/looks/:id" element={<LookEditorPage />} />
        <Route path="/options" element={<OptionsLibraryPage />} />
        <Route path="/size-charts" element={<SizeChartsPage />} />
        <Route path="/redirects" element={<RedirectsPage />} />

        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/:id" element={<CustomerDetailPage />} />
        <Route path="/trade-applications" element={<TradeApplicationsPage />} />
        <Route path="/price-lists" element={<PriceListsPage />} />

        <Route path="/returns" element={<ReturnsPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/questions" element={<QuestionsPage />} />

        <Route path="/discounts" element={<DiscountsPage />} />
        <Route path="/promotions" element={<PromotionsPage />} />
        <Route path="/carts" element={<ShopperActivityPage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />

        <Route path="/branding" element={<BrandingPage />} />
        <Route path="/storefront/home" element={<HomeLayoutPage />} />
        <Route path="/navigation" element={<NavigationPage />} />
        <Route path="/storefront/pages" element={<ContentPagesPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/new" element={<BlogEditorPage />} />
        <Route path="/blog/:id" element={<BlogEditorPage />} />
        <Route path="/gift-cards" element={<GiftCardsPage />} />
        <Route path="/affiliates" element={<AffiliatesPage />} />
        <Route path="/customer-groups" element={<CustomerGroupsPage />} />
        <Route path="/follow-ups" element={<FollowUpsPage />} />

        <Route path="/reports" element={<ReportsPage />} />

        {/* Setup: an area of its own, with its own menu (lib/nav.ts). */}
        <Route path="/settings" element={<SettingsLayout />}>
          <Route index element={<SettingsHome />} />
          <Route path="/settings/general" element={<SettingsSectionsPage page="general" />} />
          <Route path="/settings/checkout" element={<SettingsSectionsPage page="checkout" />} />
          <Route path="/settings/customers" element={<SettingsSectionsPage page="customers" />} />
          <Route path="/settings/catalog" element={<SettingsSectionsPage page="catalog" />} />
          <Route path="/settings/features" element={<FeatureFlagsPage />} />
          <Route path="/settings/payments" element={<PaymentsPage />} />
          <Route path="/settings/shipping" element={<ShippingPage />} />
          <Route path="/settings/locations" element={<StockLocationsPage />} />
          <Route path="/settings/taxes" element={<SettingsSectionsPage page="taxes" />} />
          <Route path="/settings/notifications" element={<EmailTemplatesPage />} />
          <Route path="/settings/messaging" element={<MessagingPage />} />
          <Route path="/settings/whatsapp" element={<WhatsAppPage />} />
          <Route path="/settings/messages" element={<MessagesPage />} />
          <Route path="/settings/integrations" element={<IntegrationsPage />} />
          <Route path="/settings/ai" element={<AiPage />} />
          <Route path="/settings/api-keys" element={<ApiKeysPage />} />
          <Route path="/settings/webhooks" element={<WebhooksPage />} />
          <Route path="/settings/team" element={<TeamPage />} />
          <Route path="/settings/audit" element={<AuditPage />} />
          <Route path="/settings/advanced" element={<AdvancedSettingsPage />} />
          <Route path="/settings/custom-code" element={<SettingsSectionsPage page="customCode" />} />
          <Route path="/settings/seo" element={<SeoSettingsPage />} />
        </Route>

        {/* Where the settings pages lived before; old bookmarks still land. */}
        {Object.entries(MOVED).map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/** An address the admin does not serve: said plainly, with the two ways forward. */
function NotFound() {
  const t = useT();

  return (
    <Page title={t('notFoundTitle')}>
      <Card className="flex flex-1 flex-col">
        <EmptyState
          title={t('notFoundTitle')}
          body={t('notFoundBody')}
          action={<ButtonLink to="/overview">{t('goToOverview')}</ButtonLink>}
        />
      </Card>
    </Page>
  );
}
